import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUserFromOAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuth = async () => {
      try {
        // Get session_id from URL fragment
        const hash = window.location.hash;
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];

        if (!sessionId) {
          console.error('No session_id found');
          navigate('/login');
          return;
        }

        // Exchange session_id for local session
        const response = await fetch(`${API_URL}/api/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });

        if (response.status === 403) {
          const err = await response.json();
          toast.error(err.detail || 'Registration not approved');
          navigate('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to create session');
        }

        const data = await response.json();
        
        // Handle pending approval
        if (data.status === 'pending_approval') {
          toast.info(data.message || 'Your account is pending administrator approval.');
          navigate('/login');
          return;
        }

        // Set user in context
        setUserFromOAuth(data.user);

        // Store token if provided
        if (data.token) {
          localStorage.setItem('token', data.token);
        }

        // Redirect to dashboard with user data
        navigate('/dashboard', { state: { user: data.user } });
      } catch (error) {
        console.error('OAuth callback error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processOAuth();
  }, [navigate, setUserFromOAuth]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="text-center">
        <Shield className="w-16 h-16 text-primary mx-auto mb-4 animate-pulse" />
        <p className="text-white/60 font-mono" data-testid="auth-callback-loading">Authenticating...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
